import { useMemo, type ReactElement } from 'react';
import { Provider as ReduxProvider } from 'react-redux';
import { QueryClientProvider, type QueryClient } from '@tanstack/react-query';
import { RouterProvider, type createBrowserRouter } from 'react-router-dom';

type AppRouter = ReturnType<typeof createBrowserRouter>;
import { store } from '@/shared/store';
import { createQueryClient } from '@/shared/query/queryClient';
import { AppThemeProvider } from '@/shared/theme/AppThemeProvider';
import { AuthProvider } from '@/shared/auth/AuthProvider';
import { ToastProvider } from '@/shared/ui';
import { appRouter as defaultRouter } from '@/appRouter';

export interface AppProps {
  /** Override the router in tests (e.g. inject a memory router). */
  readonly router?: AppRouter;
  /** Override the query client in tests to isolate caches. */
  readonly queryClient?: QueryClient;
}

/**
 * Top-level provider composition. Keep this lean — cross-cutting concerns
 * live inside their own provider component, not inline here.
 */
export const App = ({ router, queryClient }: AppProps = {}): ReactElement => {
  const qc = useMemo(() => queryClient ?? createQueryClient(), [queryClient]);

  return (
    <ReduxProvider store={store}>
      <QueryClientProvider client={qc}>
        <AppThemeProvider>
          <AuthProvider>
            <ToastProvider>
              <RouterProvider router={router ?? defaultRouter} />
            </ToastProvider>
          </AuthProvider>
        </AppThemeProvider>
      </QueryClientProvider>
    </ReduxProvider>
  );
};
